import React, { forwardRef, useCallback, useImperativeHandle, useRef, useState, useEffect } from 'react';
import { View, Text, Pressable, Alert, PanResponder } from 'react-native';
import Animated, {
  useAnimatedStyle,
  useSharedValue,
  withSpring,
  runOnJS,
} from 'react-native-reanimated';
import { MaterialCommunityIcons, MaterialIcons } from '@expo/vector-icons';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import { HierarchyPosition } from '@components/Hierarchy/BaseHierarchy';
import { StageItem } from './EnhancedFlowHierarchy';

export interface DraggableStageCardHandle {
  getPosition: () => HierarchyPosition;
  setPosition: (position: HierarchyPosition) => void;
  getId: () => string;
}

interface DraggableStageCardProps {
  stage: StageItem;
  initialPosition: HierarchyPosition;
  onPositionChange: (position: HierarchyPosition) => void;
  onPositionUpdate?: (position: HierarchyPosition) => void;
  dragEnabled: boolean;
  isEditMode: boolean;
  isPrimary?: boolean;
  onEditStage?: (stageId: string) => void;
  onDeleteStage?: (stageId: string) => void;
  onAddSubStage?: (parentStageId: string) => void;
  onSnap?: (targetId: string, sourceId: string) => void;
  getAllStagePositions?: () => Record<string, HierarchyPosition>;
  zoomLevel?: number;
}

const DraggableStageCard = forwardRef<DraggableStageCardHandle, DraggableStageCardProps>((props, ref) => {
  const {
    stage,
    initialPosition,
    onPositionChange,
    onPositionUpdate,
    dragEnabled,
    isEditMode,
    isPrimary = false,
    onEditStage,
    onDeleteStage,
    onAddSubStage,
    onSnap,
    getAllStagePositions,
    zoomLevel = 1
  } = props;

  // Critical safeguard: Ensure stage and stage._id exist
  if (!stage || !stage._id) {
    console.error('DraggableStageCard: Invalid stage data', { stage });
    return null;
  }

  const theme = themeSelector();

  // Animated values for position
  const translateX = useSharedValue(initialPosition.x);
  const translateY = useSharedValue(initialPosition.y);
  const scale = useSharedValue(1);
  const opacity = useSharedValue(1);

  const componentDimensions = { width: isPrimary ? 320 : 280, height: isPrimary ? 200 : 180 };

  // State
  const [isDragging, setIsDragging] = useState(false);

  // Update position when initialPosition changes
  useEffect(() => {
    translateX.value = initialPosition.x;
    translateY.value = initialPosition.y;
  }, [initialPosition.x, initialPosition.y, translateX, translateY]);

  // Report position on mount only (once per component lifecycle)
  const hasMounted = useRef(false);
  useEffect(() => {
    // Immediately report position so links can be generated
    // Only runs once on mount to avoid infinite loops
    if (!hasMounted.current) {
      onPositionChange(initialPosition);
      hasMounted.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Drag PanResponder (matching DraggableModuleCard pattern)
  const dragPanResponder = PanResponder.create({
    onStartShouldSetPanResponder: () => dragEnabled && !isDragging && isEditMode,
    onStartShouldSetPanResponderCapture: () => dragEnabled && !isDragging && isEditMode,
    onMoveShouldSetPanResponder: () => dragEnabled && isDragging && isEditMode,
    onMoveShouldSetPanResponderCapture: () => dragEnabled && isDragging && isEditMode,
    onPanResponderGrant: (event, gestureState) => {
      console.log('🎯 Drag start for stage:', stage._id);
      setIsDragging(true);
      scale.value = withSpring(1.05);
      opacity.value = withSpring(0.9);

      // Stop event propagation to prevent parent zoomable view from interfering
      event.stopPropagation();
    },
    onPanResponderMove: (event, gestureState) => {
      if (!isDragging) return;

      const { dx, dy } = gestureState;
      translateX.value = initialPosition.x + dx / zoomLevel;
      translateY.value = initialPosition.y + dy / zoomLevel;

      // Update position continuously during drag for real-time link updates
      const newPosition = {
        x: initialPosition.x + dx / zoomLevel,
        y: initialPosition.y + dy / zoomLevel
      };

      if (onPositionUpdate) {
        onPositionUpdate(newPosition);
      }

      // Stop event propagation during move
      event.stopPropagation();
    },
    onPanResponderRelease: (event, gestureState) => {
      console.log('🎯 Drag release for stage:', stage._id);

      if (!isDragging) return;

      // Snap to grid
      const gridSize = 50;
      const snappedX = Math.round(translateX.value / gridSize) * gridSize;
      const snappedY = Math.round(translateY.value / gridSize) * gridSize;

      translateX.value = withSpring(snappedX);
      translateY.value = withSpring(snappedY);

      const finalPosition = { x: snappedX, y: snappedY };
      onPositionChange(finalPosition);

      setIsDragging(false);
      scale.value = withSpring(1);
      opacity.value = withSpring(1);

      // Stop event propagation on release to prevent zoom view from reacting
      event.stopPropagation();

      // Return true to indicate we handled the gesture
      return true;
    },
    onPanResponderTerminate: (event, gestureState) => {
      // Handle case where gesture is interrupted (e.g., by another gesture)
      console.log('🎯 Drag terminated for stage:', stage._id);

      setIsDragging(false);
      scale.value = withSpring(1);
      opacity.value = withSpring(1);

      // Reset to last known good position
      translateX.value = withSpring(initialPosition.x);
      translateY.value = withSpring(initialPosition.y);
    },
    onPanResponderTerminationRequest: () => false, // Don't allow termination during drag
    onShouldBlockNativeResponder: () => true, // Always block native responders when dragging
  });

  // Expose imperative methods
  useImperativeHandle(ref, () => ({
    getPosition: () => ({
      x: translateX.value,
      y: translateY.value
    }),
    setPosition: (position: HierarchyPosition) => {
      translateX.value = withSpring(position.x);
      translateY.value = withSpring(position.y);
      onPositionChange(position);
    },
    getId: () => stage._id
  }));

  // Get stage type info
  const getStageTypeInfo = useCallback((type: string) => {
    const types = {
      greeting: { icon: 'hand-wave', color: '#10b981', bgColor: '#d1fae5' },
      introduction: { icon: 'information', color: '#3b82f6', bgColor: '#dbeafe' },
      data_collection: { icon: 'form-select', color: '#f59e0b', bgColor: '#fef3c7' },
      setup: { icon: 'cog', color: '#6366f1', bgColor: '#e0e7ff' },
      training: { icon: 'school', color: '#8b5cf6', bgColor: '#f3e8ff' },
      completion: { icon: 'check-circle', color: '#059669', bgColor: '#d1fae5' },
      diagnosis: { icon: 'stethoscope', color: '#ef4444', bgColor: '#fee2e2' },
      testing: { icon: 'test-tube', color: '#ec4899', bgColor: '#fce7f3' },
      resolution: { icon: 'check-decagram', color: '#10b981', bgColor: '#d1fae5' },
      action: { icon: 'lightning-bolt', color: '#f59e0b', bgColor: '#fef3c7' },
      decision: { icon: 'call-split', color: '#8b5cf6', bgColor: '#f3e8ff' },
      notification: { icon: 'bell', color: '#06b6d4', bgColor: '#cffafe' },
      approval: { icon: 'check-circle-outline', color: '#059669', bgColor: '#d1fae5' },
      review: { icon: 'clipboard-check', color: '#3b82f6', bgColor: '#dbeafe' },
      custom: { icon: 'puzzle', color: '#64748b', bgColor: '#f1f5f9' },
      primary: { icon: 'star', color: '#7c3aed', bgColor: '#ede9fe' }
    };
    return types[type] || types.custom;
  }, []);

  const stageTypeInfo = getStageTypeInfo(stage.type);

  // Animated styles
  const animatedStyle = useAnimatedStyle(() => {
    return {
      transform: [
        { translateX: translateX.value },
        { translateY: translateY.value },
        { scale: scale.value },
      ],
      opacity: opacity.value,
    };
  });

  const handleEditPress = useCallback(() => {
    if (onEditStage) {
      onEditStage(stage._id);
    }
  }, [stage._id, onEditStage]);

  const handleDeletePress = useCallback(() => {
    Alert.alert(
      'Delete Stage',
      `Are you sure you want to delete "${stage.title}"?`,
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Delete',
          style: 'destructive',
          onPress: () => {
            if (onDeleteStage) {
              onDeleteStage(stage._id);
            }
          },
        },
      ]
    );
  }, [stage._id, stage.title, onDeleteStage]);

  return (
    <Animated.View
      {...(dragEnabled && isEditMode ? dragPanResponder.panHandlers : {})}
      style={[
        {
          position: 'absolute',
          width: componentDimensions.width,
          height: componentDimensions.height,
          zIndex: isDragging ? 10 : 1,
        },
        animatedStyle,
      ]}
    >
      {/* Main Card Content */}
      <View
        style={{
          flex: 1,
          backgroundColor: theme ? '#ffffff' : '#1e293b',
          borderRadius: 16,
          borderWidth: 2,
          borderColor: isPrimary
            ? (theme ? stageTypeInfo.color : stageTypeInfo.color)
            : (theme ? '#e5e7eb' : '#374151'),
          shadowColor: '#000',
          shadowOffset: { width: 0, height: 4 },
          shadowOpacity: theme ? 0.1 : 0.3,
          shadowRadius: 8,
          elevation: 6,
          overflow: 'hidden'
        }}
      >
        {/* Header */}
        <View style={{
          backgroundColor: isPrimary
            ? (theme ? stageTypeInfo.bgColor : '#312e81')
            : stageTypeInfo.bgColor,
          padding: 12,
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <View style={{ flexDirection: 'row', alignItems: 'center', flex: 1, gap: 8 }}>
            <MaterialCommunityIcons
              name={stageTypeInfo.icon as any}
              size={20}
              color={isPrimary ? '#ffffff' : stageTypeInfo.color}
            />
            <View style={{ flex: 1 }}>
              <Text
                numberOfLines={1}
                style={{
                  fontSize: isPrimary ? 16 : 14,
                  fontWeight: '700',
                  color: 'grey'
                }}
              >
                {stage.title}
              </Text>
              {isPrimary && (
                <Text style={{
                  fontSize: 10,
                  fontWeight: '600',
                  color: '#ffffff',
                  textTransform: 'uppercase',
                  marginTop: 2,
                  opacity: 0.9
                }}>
                  PRIMARY FLOW
                </Text>
              )}
            </View>
          </View>

          {/* Order Badge */}
          {!isPrimary && (
            <View style={{
              backgroundColor: theme ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.1)',
              paddingHorizontal: 8,
              paddingVertical: 4,
              borderRadius: 8,
              marginLeft: 8
            }}>
              <Text style={{
                fontSize: 11,
                fontWeight: '700',
                color: theme ? '#64748b' : '#cbd5e1'
              }}>
                #{stage.order + 1}
              </Text>
            </View>
          )}

          {/* Action buttons for edit mode */}
          {isEditMode && !isPrimary && (
            <View style={{ flexDirection: 'row', gap: 4, marginLeft: 8 }}>
              <Pressable
                onPress={handleEditPress}
                style={{
                  padding: 4,
                  borderRadius: 4,
                  backgroundColor: theme ? '#f3f4f6' : '#374151',
                }}
              >
                <MaterialCommunityIcons
                  name="pencil"
                  size={14}
                  color={theme ? '#6b7280' : '#9ca3af'}
                />
              </Pressable>
              <Pressable
                onPress={handleDeletePress}
                style={{
                  padding: 4,
                  borderRadius: 4,
                  backgroundColor: theme ? '#fef2f2' : '#7f1d1d',
                }}
              >
                <MaterialCommunityIcons
                  name="delete"
                  size={14}
                  color="#ef4444"
                />
              </Pressable>
              <Pressable
                onPress={() => {
                  console.log('➕ Add sub-stage button pressed:', { stageId: stage._id, onAddSubStage: !!onAddSubStage });
                  if (onAddSubStage) {
                    onAddSubStage(stage._id);
                  } else {
                    console.error('❌ onAddSubStage is not defined');
                  }
                }}
                style={{
                  padding: 4,
                  borderRadius: 4,
                  backgroundColor: theme ? '#f0f9ff' : '#1e3a8a',
                }}
              >
                <MaterialCommunityIcons
                  name="plus"
                  size={14}
                  color="#3b82f6"
                />
              </Pressable>
            </View>
          )}
        </View>

        {/* Content */}
        <Pressable
          style={{
            flex: 1,
            padding: 12,
            justifyContent: 'space-between',
          }}
        >
          <Text
            style={{
              fontSize: 12,
              color: theme ? '#6b7280' : '#9ca3af',
              lineHeight: 16,
            }}
            numberOfLines={isPrimary ? 4 : 3}
          >
            {stage.description}
          </Text>

          {/* Stage Info Tags */}
          {!isPrimary && (
            <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
              {/* Duration */}
              {stage.config?.estimatedDuration && (
                <View style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  backgroundColor: theme ? '#f1f5f9' : '#334155',
                  paddingHorizontal: 8,
                  paddingVertical: 4,
                  borderRadius: 6,
                  gap: 4
                }}>
                  <MaterialIcons
                    name="access-time"
                    size={12}
                    color={theme ? '#64748b' : '#94a3b8'}
                  />
                  <Text style={{
                    fontSize: 11,
                    color: theme ? '#64748b' : '#94a3b8',
                    fontWeight: '600'
                  }}>
                    {stage.config.estimatedDuration}m
                  </Text>
                </View>
              )}

              {/* Required */}
              {stage.config?.required && (
                <View style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  backgroundColor: theme ? '#fee2e2' : '#7f1d1d',
                  paddingHorizontal: 8,
                  paddingVertical: 4,
                  borderRadius: 6,
                  gap: 4
                }}>
                  <MaterialCommunityIcons
                    name="alert-circle"
                    size={12}
                    color={theme ? '#dc2626' : '#fca5a5'}
                  />
                  <Text style={{
                    fontSize: 11,
                    color: theme ? '#dc2626' : '#fca5a5',
                    fontWeight: '600'
                  }}>
                    Required
                  </Text>
                </View>
              )}

              {/* Voice Config */}
              {stage.voiceConfig?.aiPrompt && (
                <View style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  backgroundColor: theme ? '#e0e7ff' : '#312e81',
                  paddingHorizontal: 8,
                  paddingVertical: 4,
                  borderRadius: 6,
                  gap: 4
                }}>
                  <MaterialIcons
                    name="mic"
                    size={12}
                    color={theme ? '#4f46e5' : '#a5b4fc'}
                  />
                  <Text style={{
                    fontSize: 11,
                    color: theme ? '#4f46e5' : '#a5b4fc',
                    fontWeight: '600'
                  }}>
                    Voice
                  </Text>
                </View>
              )}

              {/* Calendar Appointment Action Indicator */}
              {stage.stageActions?.some(action => action.actionType === 'schedule_appointment') && (
                <View style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  backgroundColor: theme ? '#dcfce7' : '#14532d',
                  paddingHorizontal: 8,
                  paddingVertical: 4,
                  borderRadius: 6,
                  gap: 4
                }}>
                  <MaterialCommunityIcons
                    name="calendar-check"
                    size={12}
                    color={theme ? '#16a34a' : '#86efac'}
                  />
                  <Text style={{
                    fontSize: 11,
                    color: theme ? '#16a34a' : '#86efac',
                    fontWeight: '600'
                  }}>
                    Appointment
                  </Text>
                </View>
              )}

              {/* Conditional Routing Indicator */}
              {stage.conditions?.nextStageRules && stage.conditions.nextStageRules.length > 0 && (
                <View style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  backgroundColor: theme ? '#f3e8ff' : '#4c1d95',
                  paddingHorizontal: 8,
                  paddingVertical: 4,
                  borderRadius: 6,
                  gap: 4
                }}>
                  <MaterialCommunityIcons
                    name="call-split"
                    size={12}
                    color={theme ? '#7c3aed' : '#c4b5fd'}
                  />
                  <Text style={{
                    fontSize: 11,
                    color: theme ? '#7c3aed' : '#c4b5fd',
                    fontWeight: '600'
                  }}>
                    {stage.conditions.nextStageRules.length} Route{stage.conditions.nextStageRules.length > 1 ? 's' : ''}
                  </Text>
                </View>
              )}

              {/* User Data Schema Link Indicator */}
              {stage.dataCollection?.form && (
                <View style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  backgroundColor: theme ? '#dbeafe' : '#1e40af',
                  paddingHorizontal: 8,
                  paddingVertical: 4,
                  borderRadius: 6,
                  gap: 4
                }}>
                  <MaterialCommunityIcons
                    name="database-sync"
                    size={12}
                    color={theme ? '#1e40af' : '#93c5fd'}
                  />
                  <Text style={{
                    fontSize: 11,
                    color: theme ? '#1e40af' : '#93c5fd',
                    fontWeight: '600'
                  }}>
                    Schema
                  </Text>
                </View>
              )}
            </View>
          )}
        </Pressable>
      </View>

      {/* Drag Handle - separate from main card (only in edit mode) */}
      {dragEnabled && isEditMode && (
        <View
          {...dragPanResponder.panHandlers}
          style={{
            position: 'absolute',
            right: -15,
            top: '50%',
            marginTop: -15,
            width: 30,
            height: 30,
            borderRadius: 15,
            backgroundColor: theme ? '#3b82f6' : '#1e40af',
            borderWidth: 2,
            borderColor: theme ? '#ffffff' : '#1e293b',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 15,
          }}
        >
          <MaterialCommunityIcons
            name="drag"
            size={16}
            color="#ffffff"
          />
        </View>
      )}
    </Animated.View>
  );
});

DraggableStageCard.displayName = 'DraggableStageCard';

export default DraggableStageCard;
